import { FC, useEffect, useState } from 'react';
import { Button, Pdf } from '../../components';
import { ButtonColor, ButtonSize } from '../../enums';
import strings from '../../language/Swedish';
import { useFilterContext } from '../../context/FilterContext';
import { useProjectContext } from '../../context/ProjectContext';
import { usePDF } from '@react-pdf/renderer';
import { useMapContext } from '@/context/MapContext';
import useUser from '@/api/queries/useUser';
import { LOADING_TYPE } from "@/constants";
import { firstLetterCaps,filterLineChartDataBasedOnDateInterval } from '@/helpers';
import {
  defaultColors,
  defaultFillOptions,
  defaultOptionsLine,
  defaultOptionsPie,
} from '@/components/pdf/components/defaultsForCharts';
import ChartJsImage from '@/components/pdf/ChartJsImage';
import useUploadPDF from '@/api/mutations/useUploadPDF';
import tw from 'twin.macro';
import styled from 'styled-components';
import { useTableCalculations } from '@/context/hooks/useTableCalculations';

interface Props {
  showRefPrice: boolean;
  reportComment: string;
  save: (force: boolean) => void;
}

const Wrapper = styled.div`
  ${tw`flex flex-row flex-wrap gap-3`};
`;

const DownLoadReportButton: FC<Props> = ({ showRefPrice, reportComment, save }) => {
  const { project, selectedReportItems } = useProjectContext();
  const { currentCoordinates, soldMarkerObjects, marketRectangleBounds } = useMapContext();
  const { soldCalculations, adsCalculations } = useTableCalculations();
  const [hasClickedReport, setHasClickedReport] = useState(false);
  const [loading, setLoading] = useState('');
  const [marketImage, setMarketImage] = useState<string | null>(null);
  const [priceImage, setPriceImage] = useState<string | null>(null);
  const [pdfUploaded, setPdfUploaded] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const { data: userData } = useUser();
  const {
    soldData: { refData, filterData },
    marketData,
  } = useFilterContext();

  const { isError, mutate: useUploadPDFMutation,isPending } = useUploadPDF({
    onSuccess: () => {
      setPdfUploaded(true);
      setUploadError(false);
      setLoading('');
    }
  });

  useEffect(() => {
    setUploadError(isError);
  }, [isError]);
  const [instance, updateInstance] = usePDF({
    document: (
      <Pdf
        {...{
          reportSettings: { showRefPrice },
          project: { ...project, reportComment },
          marketData,
          refData,
          selectedReportItems,
          soldMarkerObjects,
          currentCoordinates,
          hideClientData: true,
          userData: userData,
          priceImage,
          marketImage,
          soldCalculations,
          adsCalculations,
        }}
      />
    ),
  });

  useEffect(() => {
    if (selectedReportItems?.market && project.marketAds) {
      const sortedData = project.marketAds.ads_market_shares.sort(
        (a, b) => b.market_share - a.market_share
      );
      const labels = sortedData.slice(0, 10).map(({ firm }) => firm);
      const dataPoints = sortedData
        .slice(0, 10)
        .map(({ market_share }) => market_share);

      const sumOfOthersData =
        sortedData.length > 10
          ? project.marketAds.ads_market_shares
            .slice(10)
            .reduce((acc, item) => acc + item.market_share, 0)
          : 0;

      const myChart = new ChartJsImage();
      myChart.setConfig({
        options: defaultOptionsPie,
        type: 'pie',
        data: {
          labels: [
            ...labels,
            ...(sumOfOthersData !== 0 ? [strings.market.othersLabel] : []),
          ],
          datasets: [
            {
              label: 'chartLabel',
              data: [
                ...dataPoints,
                ...(sumOfOthersData !== 0 ? [sumOfOthersData] : []),
              ],
              borderWidth: 1,
              backgroundColor: [
                ...defaultColors.map((color) => `rgba(${color})`),
                'rgba(148, 163, 184, 0.45)',
              ],
            },
          ],
        },
      });

      myChart
        .setWidth(500)
        .setHeight(300)
        .toDataUrl()
        .then((data) => setMarketImage(data));
    }

    if (selectedReportItems?.priceDevelopment && project.indexData) {
      const data = filterLineChartDataBasedOnDateInterval(project.indexData.values,project.index_normalized_fields.reference_date_from,project.index_normalized_fields.reference_date_to)
      const dataPoints = data.map((item) => item.value);

      const getMaxValueYaxis = () => {
        const maxValue = dataPoints.reduce(
          (max, current) => (max && current && max > current ? max : current),
          dataPoints[0]
        );

        return maxValue + maxValue / 10;
      };

      const labels = data.map((item) => {
        const date = new Date(item.average_date);

        return `${strings.months[date.getMonth()]} ${date.getFullYear()}`;
      });
      const myChart = new ChartJsImage();
      myChart.setConfig({
        type: 'line',
        options: { ...defaultOptionsLine(getMaxValueYaxis()) },
        data: {
          labels: labels,
          datasets: [
            {
              data: dataPoints,
              ...defaultFillOptions,
            },
          ],
        },
      });
      myChart
        .setWidth(1024)
        .setHeight(500)
        .toDataUrl()
        .then((data) => setPriceImage(data));
    }
  }, [selectedReportItems]);

  const handleDownloadClick = (hideClientData: boolean) => {
    setLoading(hideClientData ? LOADING_TYPE.CLIENT : LOADING_TYPE.DEFAULT);
    updateInstance(
      <Pdf
      {...{
        reportSettings: { showRefPrice },
        project: { ...project, reportComment },
        marketData,
        refData,
        selectedReportItems,
        soldMarkerObjects,
        currentCoordinates,
        hideClientData: hideClientData,
        userData: userData,
        priceImage,
        marketImage,
        soldCalculations,
        adsCalculations,
      }}
      />
    );
    setTimeout(() => {
      setHasClickedReport(true);
      setLoading('');
    }, 3000);
  };

  const ExportPDF = () => {
    setLoading(LOADING_TYPE.EXPORT);
    save(true);
    const selected_columns_sold = project.soldTableReportColumns.filter((column) => column.isVisible).map((filteredColumn) => filteredColumn.id);

    const body = {
      project_id: project.id,
      ...marketRectangleBounds,
      ...selectedReportItems,
      comment: reportComment,
      selected_columns_sold: selected_columns_sold,
      show_ref_price: showRefPrice
    }
    useUploadPDFMutation(body);
  }

  useEffect(() => {
    if (!instance.loading && !instance.error && instance.url) {
      if (hasClickedReport) {
        const anchor = document.createElement('a');
        anchor.href = instance.url;
        anchor.download = `${(project.name || "").replace(/ /g, '_')}.pdf`;
        setHasClickedReport(false);
        anchor.click();
        anchor.remove();
      }
    }
  }, [instance.loading, hasClickedReport]);

  return (
    <Wrapper>
      <Button
        type="button"
        color={ButtonColor.primary}
        size={ButtonSize.large}
        className='text-ellipsis whitespace-nowrap'
        isLoading={loading === LOADING_TYPE.DEFAULT}
        onClick={() => {
          handleDownloadClick(false);
        }}>
        {strings.report.saveReportPDF}
      </Button>
      <Button
        type="button"
        color={ButtonColor.primary}
        size={ButtonSize.large}
        className='text-ellipsis whitespace-nowrap'
        isLoading={loading === LOADING_TYPE.CLIENT}
        onClick={() => {
          handleDownloadClick(true);
        }}>
        {strings.report.saveClientReportPDF}
      </Button>

      {project?.integration_name && project?.integration_id && project?.integration_upload && (
        <Button
          type="button"
          color={uploadError ? ButtonColor.warning : ButtonColor.primary}
          disabled={pdfUploaded}
          size={ButtonSize.large}
          className='text-ellipsis whitespace-nowrap'
          isLoading={loading === LOADING_TYPE.EXPORT}
          onClick={() => {
            ExportPDF();
          }}>
          {isPending && strings.data.loading}
          {pdfUploaded && !isPending && !uploadError && strings.report.pdfExported}
          {!pdfUploaded && !isPending && uploadError && strings.report.pdfUploadError}
          {!pdfUploaded && !isPending && !uploadError && strings.report.exportPDF.replace("@integration_name",firstLetterCaps(project.integration_name))}
        </Button>
      )}
      </Wrapper>
  );
};

export default DownLoadReportButton;
